import React from 'react';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import Container from '@material-ui/core/Container';
import RecentEvents from '../../components/Events/RecentEvents';

const expertise = [
  ['01 / ENGINEERING', 'Enterprise Application Development', 'Business-critical web applications, workflow automation, HR systems, payroll and operational reporting.'],
  ['02 / INTEGRATION', 'API & Interoperability Architecture', 'Secure APIs, OAuth2, gateway architecture, bank integration and cross-agency data exchange.'],
  ['03 / INTELLIGENCE', 'AI & Knowledge Systems', 'Retrieval-augmented generation, document-grounded assistants and AI-assisted productivity.'],
  ['04 / DATA', 'Databases & Migration', 'Oracle, PostgreSQL, complex data workflows, migrations, integrity and performance.'],
  ['05 / OPERATIONS', 'DevOps & Infrastructure', 'Linux servers, containers, reverse proxies, deployment, backups and production troubleshooting.'],
  ['06 / STRATEGY', 'Digital Transformation', 'Aligning technology architecture, institutional processes, governance and user needs.']
];

const experience = [
  ['DIGITAL GOVERNMENT', 'MoF Central API Gateway', 'Architecture and integration work supporting a centralized API entry point for secure interoperability with institutions and external partners.', ['WSO2', 'OAuth2', 'Nginx']],
  ['FINANCIAL INTEGRATION', 'Tax & Banking Gateways', 'Integration design and implementation supporting tax payment interfaces, banking connectivity and revenue data exchange.', ['REST APIs', 'Security', 'Payments']],
  ['ENTERPRISE SOFTWARE', 'HRIS & Payroll', 'Application engineering for employee records, attendance, contracts, leave, payroll workflows and performance reporting.', ['Ruby on Rails', 'PostgreSQL']],
  ['APPLIED ARTIFICIAL INTELLIGENCE', 'AI Knowledge Assistant', 'Retrieval-augmented generation approaches for answering organizational questions using curated documents and contextual knowledge.', ['RAG', 'LLMs', 'Knowledge Search']],
  ['TAX ADMINISTRATION', 'SIGTAS, e-Tax & ITAS', 'Tax-system engineering, database troubleshooting, system interoperability and modernization-related technical work.', ['Oracle', 'PostgreSQL', 'Integration']],
  ['DIGITAL CONTENT', 'Government Web Platforms', 'Backend development and content migration supporting public-sector publishing, structured content and digital communication.', ['Strapi', 'Contentful', 'Docker']]
];

const technologies = [
  ['Application Engineering', ['Ruby on Rails', 'REST APIs', 'Oracle APEX', 'Strapi', 'Background Jobs']],
  ['Databases & Data', ['Oracle Database', 'PostgreSQL', 'Redis', 'Data Migration', 'SQL']],
  ['Infrastructure & Security', ['Ubuntu Linux', 'Docker Swarm', 'Nginx', 'VMware', 'WSO2 API Manager']],
  ['AI & Architecture', ['RAG', 'LLM Integration', 'Solution Design', 'OAuth2', 'System Integration']]
];

export default function Album() {
  return (
    <React.Fragment>
      <CssBaseline />
      <main>
        <section className="home-hero">
          <Container maxWidth="lg">
            <div className="hero-grid">
              <div className="hero-copy">
                <span className="eyebrow">Software engineering · Solution architecture · Digital transformation</span>
                <h1>Engineering systems with <span>lasting impact.</span></h1>
                <p>
                  I am Ponciano Vieira, a Senior Software Engineer, Solution Architect, and
                  technology entrepreneur based in Timor-Leste.
                </p>
                <div className="hero-actions">
                  <Button className="primary-button" variant="contained" href="/projects">
                    Explore my work
                  </Button>
                  <Button className="secondary-button" variant="outlined" href="/contact">
                    Let's talk
                  </Button>
                </div>
                <div className="hero-stats">
                  <div><strong>Public-sector technology</strong><span>Enterprise systems and digital government</span></div>
                  <div><strong>Financial interoperability</strong><span>Secure systems and institutional integration</span></div>
                  <div><strong>Faithsasha Academy</strong><span>Accessible education and AI-powered learning</span></div>
                </div>
              </div>
              <div className="hero-profile">
                <div className="portrait-monogram">PV</div>
                <small>Building technology that makes public services work better.</small>
              </div>
            </div>
          </Container>
        </section>

        <section className="about-section" id="about">
          <Container maxWidth="lg">
            <div className="section-heading">
              <span>About me</span>
              <h2>Technology grounded in institutional reality.</h2>
            </div>
            <div className="about-grid">
              <div>
                <p>I am also a proud husband and father of three beautiful children: Faith, Sasha (Natascha), and Cairo.</p>
                <p>I currently serve as a Senior Software Engineer and Solution Architect at the Ministry of Finance of Timor-Leste, where I contribute to enterprise software engineering, digital government transformation, secure financial interoperability, and artificial intelligence solutions.</p>
                <p>I am also the CEO and Managing Director of Faithsasha Technology, the technology company behind Faithsasha Academy, an innovative digital learning platform dedicated to making education and AI-powered learning more accessible.</p>
                <p>My mission is to leverage technology, innovation, and education to empower individuals, modernize institutions, and accelerate digital transformation in Timor-Leste and beyond.</p>
              </div>
              <div className="principles-card">
                <h3>What guides my work</h3>
                <ul>
                  <li><strong>Architecture first</strong> — reliable foundations for long-term growth.</li>
                  <li><strong>Security by design</strong> — protect data and institutional trust.</li>
                  <li><strong>Interoperability</strong> — systems that communicate across boundaries.</li>
                  <li><strong>Operational ownership</strong> — deployment, monitoring and resilience.</li>
                  <li><strong>Responsible AI</strong> — augment judgment rather than replace it.</li>
                </ul>
              </div>
            </div>
          </Container>
        </section>

        <section className="expertise-section" id="expertise">
          <Container maxWidth="lg">
            <div className="section-heading">
              <span>Core expertise</span>
              <h2>From architecture to production.</h2>
              <p>Combining hands-on software engineering with system-wide architectural thinking.</p>
            </div>
            <div className="portfolio-grid">
              {expertise.map(item => (
                <article className="portfolio-card" key={item[1]}>
                  <div className="card-number">{item[0]}</div>
                  <h3>{item[1]}</h3>
                  <p>{item[2]}</p>
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="experience-section" id="experience">
          <Container maxWidth="lg">
            <div className="section-heading">
              <span>Selected professional experience</span>
              <h2>Systems and initiatives I've contributed to.</h2>
              <p>Experience highlights; these are not claims of personal or commercial ownership of Ministry of Finance systems.</p>
            </div>
            <div className="portfolio-grid">
              {experience.map(item => (
                <article className="portfolio-card" key={item[1]}>
                  <div className="card-number">{item[0]}</div>
                  <h3>{item[1]}</h3>
                  <p>{item[2]}</p>
                  <div className="tag-row">
                    {item[3].map(tag => <span key={tag}>{tag}</span>)}
                  </div>
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="technology-section" id="technology">
          <Container maxWidth="lg">
            <div className="section-heading">
              <span>Technical toolkit</span>
              <h2>Technologies I work with.</h2>
            </div>
            <div className="technology-grid">
              {technologies.map(item => (
                <article className="portfolio-card" key={item[0]}>
                  <h3>{item[0]}</h3>
                  <div className="tag-row">
                    {item[1].map(tag => <span key={tag}>{tag}</span>)}
                  </div>
                </article>
              ))}
            </div>
          </Container>
        </section>

        <Container className="projects-section" maxWidth="lg" id="projects">
          <div className="section-heading projects-heading">
            <span>Portfolio</span>
            <h2>Recent projects</h2>
          </div>
          <div className="recent-projects">
            <RecentEvents/>
          </div>
        </Container>
      </main>
    </React.Fragment>
  );
}


// WEBPACK FOOTER //
// ./src/components/Home/Main.js